// Pure helpers for the NutrientTargets page: diffing edited target rows against
// what the api returned, and lining two diets up side by side for comparison.

export const TARGET_FIELDS = ['target_value', 'upper_limit', 'lower_limit'];

export function targetKey(dietId, nutrientId) {
  return `${dietId}:${nutrientId}`;
}

// text field input arrives as '' or a string; the api stores null or a number
export function normaliseValue(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
}

export function sameValue(a, b) {
  return normaliseValue(a) === normaliseValue(b);
}

export function targetChanged(row, original) {
  if (!original) {
    return true;
  }
  return TARGET_FIELDS.some(field => !sameValue(row[field], original[field]));
}

export function indexByKey(targets) {
  return targets.reduce((acc, target) => {
    acc[targetKey(target.diet, target.nutrient_id)] = target;
    return acc;
  }, {});
}

// rows that need to be sent to the api, with values in the shape it stores
export function changedTargets(rows, originalByKey) {
  return rows
    .filter(row => targetChanged(row, originalByKey[targetKey(row.diet, row.nutrient_id)]))
    .map(row => ({
      diet: row.diet,
      nutrient_id: row.nutrient_id,
      target_value: normaliseValue(row.target_value),
      upper_limit: normaliseValue(row.upper_limit),
      lower_limit: normaliseValue(row.lower_limit),
    }));
}

// the api silently skips rows without a target value, so refuse to save them
export function rowsMissingTarget(rows) {
  return rows.filter(row => normaliseValue(row.target_value) === null);
}

function byNutrientId(a, b) {
  return a.nutrient_id - b.nutrient_id;
}

export function dietRows(rows, dietId) {
  return rows.filter(row => row.diet === dietId).sort(byNutrientId);
}

// one row per nutrient present in either diet, with the fields that differ listed
export function compareRows(rows, dietId, otherDietId) {
  const byNutrient = {};
  rows
    .filter(row => row.diet === dietId || row.diet === otherDietId)
    .forEach(row => {
      const entry = byNutrient[row.nutrient_id] || (byNutrient[row.nutrient_id] = {
        nutrient_id: row.nutrient_id,
        mine: null,
        other: null,
      });
      if (row.diet === dietId) {
        entry.mine = row;
      } else {
        entry.other = row;
      }
    });

  return Object.values(byNutrient)
    .sort(byNutrientId)
    .map(entry => ({
      ...entry,
      differences: TARGET_FIELDS.filter(field => !sameValue(
        entry.mine && entry.mine[field],
        entry.other && entry.other[field],
      )),
    }));
}

export function newTargetRow(dietId, nutrientId) {
  return {
    diet: dietId,
    nutrient_id: nutrientId,
    target_value: null,
    upper_limit: null,
    lower_limit: null,
  };
}

// keep only what can be part of a non-negative decimal as the user types
export function sanitiseNumberText(text) {
  const digitsAndDots = String(text === null || text === undefined ? '' : text).replace(/[^0-9.]/g, '');
  const [whole, ...fraction] = digitsAndDots.split('.');
  return fraction.length > 0 ? `${whole}.${fraction.join('')}` : whole;
}
